import { useMutation, useQueryClient } from "@tanstack/react-query";
import { WalletMinimal } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ChargeItemDefinitionPicker } from "@/components/Common/ChargeItemDefinitionPicker";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChargeItemDefinitionBase } from "@/types/billing/chargeItemDefinition/chargeItemDefinition";
import { FacilityRead } from "@/types/facility/facility";
import facilityApi from "@/types/facility/facilityApi";
import mutate from "@/Utils/request/mutate";

interface Props {
  facility: FacilityRead;
}

export default function FacilityConsultationChargeSelector({
  facility,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [definition, setDefinition] = useState<ChargeItemDefinitionBase | null>(
    facility.consultation_charge_item_definition ?? null,
  );
  const queryClient = useQueryClient();

  const { mutate: save, isPending } = useMutation({
    mutationFn: mutate(facilityApi.setConsultationChargeItemDefinition, {
      pathParams: { facilityId: facility.id },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility", facility.id] });
      toast.success("Consultation charge updated");
      setOpen(false);
    },
  });

  const reset = () =>
    setDefinition(facility.consultation_charge_item_definition ?? null);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <WalletMinimal className="size-4" />
          Manage consultation charge
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[90%] sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Consultation charge</SheetTitle>
          <SheetDescription>
            Select the charge definition used for OPD consultation billing.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex flex-col gap-6">
          <ChargeItemDefinitionPicker
            facilityId={facility.id}
            value={definition ?? undefined}
            onValueChange={(value) =>
              setDefinition((Array.isArray(value) ? value[0] : value) ?? null)
            }
            placeholder={t("select_charge_item_definition")}
            className="w-full"
            showCreateButton
          />
          <div className="flex justify-between gap-4 border-t pt-4">
            <Button
              variant="outline"
              onClick={() =>
                save({ consultation_charge_item_definition: null })
              }
              disabled={
                isPending || !facility.consultation_charge_item_definition
              }
            >
              Clear consultation charge
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setOpen(false)}>
                {t("cancel")}
              </Button>
              <Button
                onClick={() =>
                  save({
                    consultation_charge_item_definition:
                      definition?.slug ?? null,
                  })
                }
                disabled={isPending}
              >
                {t("save")}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
